import { SettingsService } from '../../../../../shared/services/settings.service';
import type { CameraSortMode } from '../components/sort-mode-sheet/sort-mode.type';
import type { HttpHeaders, HttpResponse } from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import type { CameraRestResponse } from '@hunting-nature/shared-core/interfaces/camera-rest-response.interface';
import type { CameraRest } from '@hunting-nature/shared-core/interfaces/camera-rest.interface';
import type { ImageResponse } from '@hunting-nature/shared-core/interfaces/image-response.interface';
import type { Observable } from 'rxjs';
import { catchError, expand, map, of, reduce, switchMap, EMPTY } from 'rxjs';

import { AppConfigLoader } from '../../../../../config/app-config.loader';
import { RestService } from '../../../../../shared/services/rest-service';
import type { FilterCamera } from '../redux/filter-camera.interface';

/**
 * Default page size for camera pagination
 */
const CAMERAS_PAGE_SIZE = 10;

/**
 * Filter parameters for fetching detection tags based on active filter
 */
export interface DetectionTagsFilter {
  /** Array of camera IDs to filter by (if undefined, uses 'all') */
  cameraIds?: Array<string>;
  /** Start date for the filter (ISO 8601 string) */
  dateFrom?: string;
  /** End date for the filter (ISO 8601 string) */
  dateTo?: string;
}

/**
 * Service responsible for managing filter data for the gallery.
 * This service provides isolated copies of backend calls so you can modify and extend them
 * without affecting other parts of the app.
 *
 * Current API calls:
 * - fetchCameras$(): Fetches all cameras the user can access
 * - fetchAllDetectionTags$(): Fetches all available detection tags
 * - fetchDetectionTagsForFilter$(): Fetches tags matching active filter
 */
@Injectable({
  providedIn: 'root',
})
export class GalleryFilterDataService {
  private readonly httpClient = inject(HttpClient);
  private readonly appConfigLoader = inject(AppConfigLoader);
  private readonly restService = inject(RestService);
  private readonly settingsService = inject(SettingsService);

  /**
   * Loads the persisted camera filter sort mode from SettingsService.
   */
  public getCameraFilterSortMode$(): Promise<CameraSortMode> {
    return this.settingsService.getCameraFilterSortMode$();
  }

  /**
   * Persists the camera filter sort mode using SettingsService.
   */
  public setCameraFilterSortMode$(sortMode: CameraSortMode): Promise<void> {
    return this.settingsService.setCameraFilterSortMode$(sortMode);
  }

  /**
   * Loads the persisted 'show no media options' flag from SettingsService.
   */
  public getCameraFilterShowNoMediaOptions$(): Promise<boolean> {
    return this.settingsService.getCameraFilterShowNoMediaOptions$();
  }

  /**
   * Persists the 'show no media options' flag using SettingsService.
   */
  public setCameraFilterShowNoMediaOptions$(show: boolean): Promise<void> {
    return this.settingsService.setCameraFilterShowNoMediaOptions$(show);
  }

  public fetchFilterCameras$(): Observable<{ cameras: Array<FilterCamera>; images: Array<ImageResponse> }> {
    return this.fetchCameras$().pipe(switchMap(({ cameras }) => this.buildFilterCameraData$(cameras)));
  }

  /**
   * Fetches all cameras the user can access using proper pagination.
   * This implementation automatically fetches all pages until all cameras are loaded.
   * DUPLICATED IMPLEMENTATION from RestService.getCamerasUserCanAccess$() with proper pagination.
   * This is your own independent copy that won't affect the rest of the app.
   *
   * @param pageSize - Number of cameras to fetch per request (default: 10)
   * @returns Observable of camera response with ALL cameras loaded and total count
   */
  public fetchCameras$(pageSize: number = CAMERAS_PAGE_SIZE): Observable<CameraRestResponse> {
    return this.fetchCameraPage$(0, pageSize).pipe(
      expand((response: CameraRestResponse) => {
        const fetchedCount = response.cameras.length;
        const totalCount = response.totalNoCameras;

        // If we've fetched all cameras, stop expanding
        if (fetchedCount >= totalCount) {
          return EMPTY;
        }

        // Otherwise, fetch the next page
        const nextOffset = fetchedCount;
        return this.fetchCameraPage$(nextOffset, pageSize).pipe(
          map(nextPageResponse => ({
            cameras: [...response.cameras, ...nextPageResponse.cameras],
            totalNoCameras: nextPageResponse.totalNoCameras,
          }))
        );
      }),
      // Take the final accumulated result
      reduce((accumulator, current) => current),
      catchError((error: unknown) => {
        console.error('[GalleryFilterDataService] fetchCameras error:', error);
        throw error;
      })
    );
  }

  private buildFilterCameraData$(cameras: Array<CameraRest>): Observable<{ cameras: Array<FilterCamera>; images: Array<ImageResponse> }> {
    const imageIds = [...new Set(cameras.flatMap(camera => (camera.latestImageId ? [camera.latestImageId] : [])))];

    if (imageIds.length === 0) {
      return of({
        cameras: cameras.map(camera => this.toFilterCamera(camera)),
        images: [],
      });
    }

    return this.restService.fetchImagesByImageIds$(imageIds).pipe(
      map(images => {
        const lastImageReceivedAtById = new Map(images.map(image => [image.id, image.snap_time]));
        return {
          cameras: cameras.map(camera => this.toFilterCamera(camera, lastImageReceivedAtById)),
          images,
        };
      }),
      catchError((error: unknown) => {
        console.error('[GalleryFilterDataService] buildFilterCameraData error:', error);
        return of({
          cameras: cameras.map(camera => this.toFilterCamera(camera)),
          images: [],
        });
      })
    );
  }

  /**
   * Fetches a single page of cameras.
   * Internal helper method used by fetchCameras$() for pagination.
   *
   * @param startIndex - Starting index for this page
   * @param limit - Number of cameras to fetch in this page
   * @returns Observable of camera response for this page
   */
  private fetchCameraPage$(startIndex: number, limit: number): Observable<CameraRestResponse> {
    const params = { offset: startIndex, limit: limit };
    return this.httpClient.get<Array<CameraRest>>(`${this.appConfigLoader.activeConfig().backend.apiUrl}/cameras`, { params, observe: 'response' }).pipe(
      map((response: HttpResponse<Array<CameraRest>>) => {
        const cameras = response.body ?? [];
        return {
          cameras,
          totalNoCameras: this.getHeaderValueInt(response.headers, 'x-total-count', cameras.length ?? 0),
        };
      })
    );
  }

  /**
   * Fetches all available detection tags from the backend (no filter applied).
   * DUPLICATED IMPLEMENTATION from RestService.getTags$()
   * This is your own independent copy that won't affect the rest of the app.
   *
   * @returns Observable of array of all available tag strings
   */
  public fetchAllDetectionTags$(): Observable<Array<string>> {
    const url = `${this.appConfigLoader.activeConfig().backend.apiUrl}/images/tags`;

    return this.httpClient
      .post<Array<string>>(url, {
        collection: { name: 'all' },
        // TODO: remove date once it became optional in the backend
        date: {
          from: new Date('2000-01-01').toISOString(),
          to: new Date().toISOString(),
        },
      })
      .pipe(
        catchError((error: unknown) => {
          console.error('[GalleryFilterDataService] fetchAllDetectionTags error:', error);
          throw error;
        })
      );
  }

  /**
   * Fetches detection tags filtered by the provided filter parameters.
   * Use this to get tags that match the currently active filter (e.g., selected cameras, date range).
   * DUPLICATED IMPLEMENTATION from RestService.getTags$() with filter support.
   * This is your own independent copy that won't affect the rest of the app.
   *
   * @param filter - Filter parameters (camera IDs, date range)
   * @returns Observable of array of filtered tag strings
   */
  public fetchDetectionTagsForFilter$(filter: DetectionTagsFilter): Observable<Array<string>> {
    const url = `${this.appConfigLoader.activeConfig().backend.apiUrl}/images/tags`;

    // Build the collection parameter based on filter
    const collection = filter.cameraIds && filter.cameraIds.length > 0 ? { cameraIds: filter.cameraIds } : { name: 'all' };

    // Build the date parameter based on filter
    const date = {
      from: filter.dateFrom ?? new Date('2000-01-01').toISOString(),
      to: filter.dateTo ?? new Date().toISOString(),
    };

    return this.httpClient
      .post<Array<string>>(url, {
        collection,
        date,
      })
      .pipe(
        catchError((error: unknown) => {
          console.error('[GalleryFilterDataService] fetchDetectionTagsForFilter error:', error);
          throw error;
        })
      );
  }

  /**
   * Helper method to parse integer from HTTP headers.
   * Used by fetchCameras$ to extract the total count from response headers.
   */
  private getHeaderValueInt(headers: HttpHeaders, headerKey: string, defaultValue: number): number {
    const totalCount = headers.get(headerKey);
    return totalCount ? Number.parseInt(totalCount) : defaultValue;
  }

  private getBackendLastImageReceivedAt(camera: CameraRest): string | undefined {
    return (camera as CameraRest & { lastImageReceivedAt?: string }).lastImageReceivedAt;
  }

  private toFilterCamera(camera: CameraRest, lastImageReceivedAtById = new Map<string, string>()): FilterCamera {
    return {
      ...camera,
      lastImageReceivedAt: this.getBackendLastImageReceivedAt(camera) ?? (camera.latestImageId ? lastImageReceivedAtById.get(camera.latestImageId) : undefined),
    };
  }
}
